import {
    addSeconds,
    differenceInMilliseconds,
    isBefore,
    parse
} from 'date-fns';
import { logout } from './actions';
import { getStore } from '../../../redux/store-registry';
import {
    FETCH_USER_PERMISSIONS_SUCCESS,
    LOGIN_FAIL,
    LOGIN_SUCCESS,
    LOGOUT,
    RESET_USERNAME_PASSWORD,
    SESSION_ENDED,
    SET_PASSWORD,
    SET_USERNAME
} from './actionTypes';
import jwtDecode from 'jwt-decode';
import {
    clearLocalCredentials,
    isLocalLogin,
    loginExpiresInSeconds,
    usesLocalAuth
} from '../local-auth';

const AUTH_DATA = 'AUTH_DATA';

// uncomment to enable all permissions:
// const additionalPermissions = _.values(PermissionEnum).map(p => `${p}:ALL:ALL`);
const additionalPermissions = [];

function isLoggedIn() {
    const storedAuthData = JSON.parse(localStorage.getItem(AUTH_DATA));
    return (
        storedAuthData &&
        // A local login has no token; its credential is the password of this tab (local-auth.js).
        (storedAuthData.token || usesLocalAuth()) &&
        isBefore(new Date(), parse(storedAuthData.expires_at))
    );
}

// setTimeout runs a callback at once if its delay is longer than this (about
// 24.8 days), so longer sessions are logged out at this point instead.
const maxTimeoutDelay = 2147483647;

// Logs out when the token expires. The delay is measured from now, so a session
// restored from localStorage logs out at its real expiry rather than a full
// token lifetime after the page was reloaded. An unreadable expiry logs out at
// once, as a reload would.
const makeAutoLogoutHandler = expiresAt => {
    const delay = differenceInMilliseconds(parse(expiresAt), new Date());

    return {
        autoLogoutHandler: setTimeout(() => {
            const store = getStore();
            store && store.dispatch(logout());
        }, Math.min(Math.max(delay, 0), maxTimeoutDelay) || 0)
    };
};

// What leaving a session forgets: the stored login, the password of a local login, and the timer that logs out when the token expires.
const forgetSession = state => {
    localStorage.removeItem(AUTH_DATA);
    clearLocalCredentials();

    if (state.autoLogoutHandler) {
        clearTimeout(state.autoLogoutHandler);
    }
};

const makeInitialState = () => {
    if (isLoggedIn()) {
        const authData = JSON.parse(localStorage.getItem(AUTH_DATA));

        return {
            ...authData,
            ...makeAutoLogoutHandler(authData.expires_at)
        };
    } else {
        return {
            autoLogoutHandler: null,
            authenticated: false,
            token: '',
            decodedToken: null,
            expires_at: '',
            username: '',
            password: '',
            user: {
                id: '',
                firstName: '',
                lastName: '',
                email: '',
                company: ''
            },
            error: null
        };
    }
};

export function authReducer(state = makeInitialState(), action) {
    switch (action.type) {
        case SET_USERNAME: {
            return {
                ...state,
                username: action.payload
            };
        }
        case SET_PASSWORD: {
            return {
                ...state,
                password: action.payload
            };
        }
        case LOGIN_SUCCESS: {
            const { data } = action.payload;

            const expiresInSeconds = loginExpiresInSeconds(data);
            const expiresAt = addSeconds(new Date(), expiresInSeconds);
            const expiresIn = expiresInSeconds * 1000; // (expiresIn arrives as seconds)

            // todo: Following function should be an effect / saga
            // (A local login has no token to decode; the permissions come from the user, as for any login.)
            let decodedTokenWithMockedPermissions = isLocalLogin(data)
                ? {}
                : jwtDecode(data.accessToken);

            decodedTokenWithMockedPermissions = {
                ...decodedTokenWithMockedPermissions,
                permissions: [
                    ...(decodedTokenWithMockedPermissions.permissions || []),
                    ...additionalPermissions
                ]
            };

            const newAuthenticationState = {
                expiresIn,
                authenticated: true,
                token: data.accessToken,
                decodedToken: decodedTokenWithMockedPermissions,
                expires_at: expiresAt,
                user: {
                    id: data.admin.id,
                    firstName: data.admin.firstName,
                    lastName: data.admin.lastName,
                    email: data.admin.email,
                    company: data.admin.company || 'TheGamingCompany'
                },
                ...makeAutoLogoutHandler(expiresAt)
            };
            localStorage.setItem(
                AUTH_DATA,
                JSON.stringify(newAuthenticationState)
            );
            return {
                ...state,
                ...newAuthenticationState,
                // Not in the AUTH_DATA stored above: it says why the last session ended, and this one has begun
                sessionEnded: false
            };
        }
        case RESET_USERNAME_PASSWORD: {
            return {
                ...state,
                username: '',
                password: ''
            };
        }
        case FETCH_USER_PERMISSIONS_SUCCESS: {
            const parsedLocalStorageInfo = JSON.parse(
                localStorage.getItem(AUTH_DATA)
            );

            // The user logged out while the permissions were being fetched.
            if (!state.authenticated || !parsedLocalStorageInfo) {
                return state;
            }

            const updatedLocalStorageInfo = {
                ...parsedLocalStorageInfo,
                decodedToken: {
                    ...parsedLocalStorageInfo.decodedToken,
                    permissions: action.payload.permissions
                }
            };
            localStorage.setItem(
                AUTH_DATA,
                JSON.stringify(updatedLocalStorageInfo)
            );
            return {
                ...state,
                decodedToken: updatedLocalStorageInfo.decodedToken
            };
        }
        case LOGIN_FAIL: {
            localStorage.removeItem(AUTH_DATA);
            return {
                ...makeInitialState(),
                error: action.payload
            };
        }
        case LOGOUT: {
            forgetSession(state);

            return makeInitialState();
        }
        case SESSION_ENDED: {
            // A refusal that arrives after the user left (a request still in the air) changes nothing, and says nothing
            if (!state.authenticated) {
                return state;
            }
            forgetSession(state);

            // The login page says why
            return { ...makeInitialState(), sessionEnded: true };
        }
        default:
            return state;
    }
}
